import type { BrandConfig, BrandId } from "@/types"

/**
 * Catálogo de marcas oficiales de Oficina Virtual.
 * Debe permanecer alineado con la tabla `brands` del backend (migración
 * 010_multitenancy) y con `HOST_TO_BRAND` del BrandResolverMiddleware.
 *
 * Colores oficiales:
 *  - pandamovil: azul corporativo
 *  - celink:     verde Celink (memoria mexsim-crm-tenant-setup #3B7D23)
 *  - claricel:   naranja Claricel (legacy "cheap")
 *  - nxum:       morado #6E3984 (memoria nxum-tenant.md)
 *  - cristocel:  azul #1A4D8F (alineado con crm.cristocel.com)
 *  - infynit:    cian #5DD4F9 + marino #00457C (alineado con crm.infynit.mx / app_web tenants.json)
 *  - figou:      marca interna figou (DiDi + iGou) servida en oficina.igou.mx con
 *               branding iGou: marino #2E3192 (alineado con crm.igou.mx tenants.json)
 *  - beonline:   azul marino + amarillo
 *  - baxnet:     azul #2196F3 + azul oscuro #1976D2. Instalación AISLADA
 *               (ofv.baxnet.mx, BD y API propios; middleware api.baxnet.mx)
 */
export const brands: Record<BrandId, BrandConfig> = {
  pandamovil: {
    id: "pandamovil",
    name: "Panda Móvil",
    domain: "red.pandamovil.mx",
    logo: "/brands/pandamovil/logo.png",
    logoLight: "/brands/pandamovil/logo-light.png",
    primaryColor: "#F93549",
    description: "Oficina Virtual Panda Móvil",
  },
  celink: {
    id: "celink",
    name: "Celink",
    domain: "red.celink.mx",
    logo: "/brands/celink/logo.png",
    logoLight: "/brands/celink/logo-light.png",
    primaryColor: "#22c55e",
    description: "Oficina Virtual Celink",
  },
  claricel: {
    id: "claricel",
    name: "Claricel",
    domain: "red.claricel.net",
    logo: "/brands/claricel/logo.png",
    logoLight: "/brands/claricel/logo-light.png",
    primaryColor: "#f97316",
    description: "Oficina Virtual Claricel",
  },
  nxum: {
    id: "nxum",
    name: "NXUM",
    domain: "red.nxum.mx",
    logo: "/brands/nxum/logo.png",
    logoLight: "/brands/nxum/logo-light.png",
    primaryColor: "#6E3984",
    description: "Oficina Virtual NXUM",
  },
  cristocel: {
    id: "cristocel",
    name: "CristoCel",
    domain: "oficina.cristocel.com",
    logo: "/brands/cristocel/logo.png",
    logoLight: "/brands/cristocel/logo-light.png",
    primaryColor: "#1A4D8F",
    description: "Oficina Virtual CristoCel",
  },
  infynit: {
    id: "infynit",
    name: "Infynit",
    domain: "mayorista.infynit.mx",
    logo: "/brands/infynit/logo.png",
    logoLight: "/brands/infynit/logo-light.png",
    primaryColor: "#5DD4F9",
    description: "Oficina Virtual Infynit",
  },
  figou: {
    id: "figou",
    name: "iGou",
    domain: "oficina.igou.mx",
    logo: "/brands/figou/logo.png",
    logoLight: "/brands/figou/logo-light.png",
    primaryColor: "#2E3192",
    description: "Oficina Virtual iGou",
  },
  beonline: {
    id: "beonline",
    name: "Be Online",
    domain: "red.beonline.com.mx",
    logo: "/brands/beonline/logo.png",
    logoLight: "/brands/beonline/logo-light.png",
    primaryColor: "#131e29",
    description: "Oficina Virtual Be Online",
  },
  baxnet: {
    id: "baxnet",
    name: "Baxnet",
    domain: "ofv.baxnet.mx",
    // logo = tinta oscura (fondo claro); logo-light = blanco (fondo oscuro).
    logo: "/brands/baxnet/logo.png",
    logoLight: "/brands/baxnet/logo-light.png",
    primaryColor: "#2196F3",
    description: "Oficina Virtual Baxnet",
  },
}

export const defaultBrand: BrandId = "pandamovil"

/**
 * Mapping host → brand. Único punto de verdad en el frontend; refleja
 * `HOST_TO_BRAND` del backend (`api/core/brand_resolver.py`).
 *
 * No usar `hostname.includes(...)` (frágil con substrings — celink/claricel).
 */
const HOST_TO_BRAND: Record<string, BrandId> = {
  // Producción
  "red.pandamovil.mx": "pandamovil",
  "red.celink.mx": "celink",
  "red.claricel.net": "claricel",
  "red.nxum.mx": "nxum",
  "red.cristocel.com": "cristocel",
  "oficina.cristocel.com": "cristocel",
  "mayorista.infynit.mx": "infynit",
  "red.figou.mx": "figou",
  "oficina.igou.mx": "figou",
  "red.beonline.com.mx": "beonline",
  "ofv.baxnet.mx": "baxnet",
  // Desarrollo local
  "localhost": "pandamovil",
  "127.0.0.1": "pandamovil",
}

/**
 * Resuelve la marca a partir del hostname del navegador.
 * Compara con dict literal (case-insensitive). Fallback a defaultBrand
 * si el host no está mapeado.
 */
export function getBrandFromHostname(): BrandId {
  if (typeof window === "undefined") return defaultBrand
  const hostname = window.location.hostname.toLowerCase()
  return HOST_TO_BRAND[hostname] ?? defaultBrand
}

export function getBrandConfig(brandId: BrandId): BrandConfig {
  return brands[brandId] ?? brands[defaultBrand]
}
